import React, {useState} from 'react';
import clsx from 'clsx';
import {ERROR} from '@di-shared/const';

enum GRAPH_COLOR {
  BAD = 'var(--quaternary-low)',
  NORMAL = 'var(--tertiary)',
  GOOD = 'var(--graph-gauge-active)',
}

/**
 * 1) 65점 ~ 74점: 컬러 - red
 * 2) 75점 ~ 88점: 컬러 - yellow
 * 3) 89점 ~ 100점: 컬러 - green
 */
const SCORE_THRESHOLD = {
  NORMAL: 88,
  BAD: 74,
};

interface GraphData {
  score: number;
  monthName: string;
}

interface SafeDrivingScoreDetailGraphEuProps {
  data: [GraphData, GraphData] | [GraphData];
}

function getGraphColor(score: number) {
  if (score > SCORE_THRESHOLD.NORMAL) {
    return GRAPH_COLOR.GOOD;
  }
  if (score > SCORE_THRESHOLD.BAD) {
    return GRAPH_COLOR.NORMAL;
  }
  return GRAPH_COLOR.BAD;
}

function SafeDrivingScoreDetailGraphEu({data}: SafeDrivingScoreDetailGraphEuProps) {
  const isSingleGraph = data.length === 1;
  const [selectedGraph, setSelectedGraph] = useState(isSingleGraph ? 0 : 1);
  const scoreScaleFactor = 0.6;
  const width = 220;
  const height = 100;
  const gap = 13;
  const firstLevel = data[0].score * scoreScaleFactor;
  const secondLevel = data[1] ? data[1].score * scoreScaleFactor : 0;
  const linePoint = [
    `5,${height - gap - firstLevel}`,
    `70,${height - gap - firstLevel}`,
    `150,${height - gap - secondLevel}`,
    `215,${height - gap - secondLevel}`,
  ];
  const circlePoint = [
    {x: 70, y: height - gap - firstLevel},
    {x: 150, y: height - gap - secondLevel},
  ];
  const lineGraphColor = data.map(({score}) => getGraphColor(score))[selectedGraph];
  return (
    <div className={clsx('statistics_graph_area graph_full_type', {graph_score_detail: !isSingleGraph})}>
      <div className="bar_graph">
        {data.map(({score, monthName}, index) => (
          <div
            key={index}
            className={clsx('bar_graph_item', {
              'is-active': selectedGraph === index || isSingleGraph,
              type_good: score > SCORE_THRESHOLD.NORMAL,
              type_caution: score <= SCORE_THRESHOLD.NORMAL && score > SCORE_THRESHOLD.BAD,
              type_warning: score <= SCORE_THRESHOLD.BAD,
            })}
          >
            <span className="speech_bubble">{score}</span>
            <span
              className="graph_bar"
              style={{height: score * scoreScaleFactor}}
              onClick={() => setSelectedGraph(index)}
            />
            <span className="graph_title">{monthName}</span>
          </div>
        ))}

        <div
          className="bar_graph_average"
          style={{top: height - (firstLevel + secondLevel) / 2}}
        >
          <span className="average_line"></span>
        </div>
      </div>

      {!isSingleGraph && (
        <div
          className="line_graph"
          style={{
            height: height,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <svg
            width={width}
            height={height}
            xmlns="http://www.w3.org/2000/svg"
          >
            <polyline
              fill="none"
              stroke={lineGraphColor}
              strokeWidth="3"
              strokeLinecap="round"
              points={linePoint.join(' ')}
            />
            {circlePoint.map(({x, y}) => (
              <circle
                key={x.toString() + y}
                cx={x}
                cy={y}
                r="5"
                fill="var(--surface)"
                stroke={lineGraphColor}
                strokeWidth="2"
              />
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

function SafeDrivingScoreDetailGraphEuLoading() {
  return (
    <div
      className="statistics_graph_area graph_full_type graph_score_detail skeleton"
      style={{paddingTop: '3px'}}
    >
      <div className="bar_graph">
        <div className="bar_graph_item">
          <span
            className="graph_bar"
            style={{height: 5}}
          />
          <span className="graph_title">{ERROR.DASH}</span>
        </div>
        <div className="bar_graph_item">
          <span
            className="graph_bar"
            style={{height: 5}}
          />
          <span className="graph_title">{ERROR.DASH}</span>
        </div>

        <div
          className="bar_graph_average"
          style={{top: 100}}
        >
          <span className="average_line"></span>
        </div>
      </div>
    </div>
  );
}

function SafeDrivingScoreDetailGraphEuError() {
  return (
    <div className="statistics_graph_area graph_full_type graph_score_detail">
      <div className="bar_graph">
        <div className="bar_graph_item">
          <span
            className="graph_bar"
            style={{height: 5}}
          />
          <span className="graph_title">{ERROR.DASH}</span>
        </div>
        <div className="bar_graph_item">
          <span
            className="graph_bar"
            style={{height: 5}}
          />
          <span className="graph_title">{ERROR.DASH}</span>
        </div>

        <div
          className="bar_graph_average"
          style={{top: 100}}
        >
          <span className="average_line"></span>
        </div>
      </div>
    </div>
  );
}

SafeDrivingScoreDetailGraphEu.Error = SafeDrivingScoreDetailGraphEuError;
SafeDrivingScoreDetailGraphEu.Loading = SafeDrivingScoreDetailGraphEuLoading;

export default SafeDrivingScoreDetailGraphEu;
